/* PostHog + GA4 event tracking helpers */

import {
  prepareAnalyticsProperties,
  sanitiseAnalyticsProperties,
  type AnalyticsEventName,
  type AnalyticsProperties,
} from "@/lib/analytics/events";
import { readAnalyticsConsent } from "@/lib/analytics-consent";

declare global {
  interface Window {
    posthog?: {
      capture: (event: string, properties?: Record<string, unknown>) => void;
      identify: (id: string, properties?: Record<string, unknown>) => void;
      reset: () => void;
      opt_in_capturing?: () => void;
      opt_out_capturing?: (options?: { clear_persistence?: boolean }) => void;
      stopSessionRecording?: () => void;
      __loaded?: boolean;
    };
    gtag?: (...args: unknown[]) => void;
  }
}

export const POSTHOG_READY_EVENT = "peakwave:posthog-ready";

const PRODUCTION_ANALYTICS_HOSTS = new Set(["peakwave.co", "www.peakwave.co"]);
const MAX_PENDING_ANALYTICS_OPERATIONS = 200;

type PendingAnalyticsOperation =
  | {
      kind: "capture";
      event: AnalyticsEventName;
      properties: AnalyticsProperties;
    }
  | {
      kind: "identify";
      id: string;
      properties: AnalyticsProperties;
    }
  | {
      kind: "reset";
    };

let pendingAnalyticsOperations: PendingAnalyticsOperation[] = [];
let readyListenerAttached = false;

/** Client-side PostHog capture. Events fired before the SDK is ready are held
 *  in a small in-memory queue and delivered in order after initialisation. */
export function trackEvent(
  event: AnalyticsEventName,
  properties?: Record<string, unknown>,
): boolean {
  if (!clientAnalyticsEnabled()) return false;

  const preparedProperties = prepareAnalyticsProperties(
    event,
    properties,
    "client",
  );

  try {
    const ph = window.posthog;
    if (!postHogReady(ph)) {
      enqueueAnalyticsOperation({
        kind: "capture",
        event,
        properties: preparedProperties,
      });
      return true;
    }
    flushPendingAnalyticsOperations();
    ph.capture(event, preparedProperties);
    return true;
  } catch (err) {
    console.warn("[analytics] trackEvent failed", err);
    return false;
  }
}

/** Client-side PostHog identify links the anonymous client distinct id to the
 * authenticated user id so client funnel events stitch to server events. */
export function identifyUser(
  id: string,
  properties?: Record<string, unknown>,
): boolean {
  if (!clientAnalyticsEnabled() || !id) return false;
  const preparedProperties = sanitiseAnalyticsProperties(properties);
  try {
    const ph = window.posthog;
    if (!postHogReady(ph)) {
      enqueueAnalyticsOperation({
        kind: "identify",
        id,
        properties: preparedProperties,
      });
      return true;
    }
    flushPendingAnalyticsOperations();
    ph.identify(id, preparedProperties);
    return true;
  } catch (err) {
    console.warn("[analytics] identify failed", err);
    return false;
  }
}

/** Clear PostHog's identified-user state before the authenticated session ends. */
export function resetAnalyticsIdentity(): boolean {
  if (!clientAnalyticsEnabled()) return false;
  pendingAnalyticsOperations = [];
  try {
    const ph = window.posthog;
    if (!postHogReady(ph)) {
      enqueueAnalyticsOperation({ kind: "reset" });
      return true;
    }
    ph.reset();
    return true;
  } catch (err) {
    console.warn("[analytics] reset failed", err);
    return false;
  }
}

function clientAnalyticsEnabled(): boolean {
  if (typeof window === "undefined") return false;
  const hostname = window.location?.hostname;
  try {
    const enabled = Boolean(hostname && PRODUCTION_ANALYTICS_HOSTS.has(hostname)
      && readAnalyticsConsent() === "accepted");
    if (!enabled) pendingAnalyticsOperations = [];
    return enabled;
  } catch {
    pendingAnalyticsOperations = [];
    return false;
  }
}

function postHogReady(
  posthog: Window["posthog"],
): posthog is NonNullable<Window["posthog"]> {
  return Boolean(
    posthog
      && posthog.__loaded
      && typeof posthog.capture === "function"
      && typeof posthog.identify === "function"
      && typeof posthog.reset === "function",
  );
}

function enqueueAnalyticsOperation(operation: PendingAnalyticsOperation): void {
  if (pendingAnalyticsOperations.length >= MAX_PENDING_ANALYTICS_OPERATIONS) {
    pendingAnalyticsOperations.shift();
  }
  pendingAnalyticsOperations.push(operation);
  attachPostHogReadyListener();
}

function attachPostHogReadyListener(): void {
  if (readyListenerAttached || typeof window.addEventListener !== "function") {
    return;
  }
  readyListenerAttached = true;
  window.addEventListener(
    POSTHOG_READY_EVENT,
    () => {
      readyListenerAttached = false;
      flushPendingAnalyticsOperations();
    },
    { once: true },
  );
}

function flushPendingAnalyticsOperations(): void {
  // Consent can change in another tab while the SDK is loading. The local
  // consent-change event does not reach this tab, so recheck before delivery.
  if (!clientAnalyticsEnabled()) return;
  const ph = window.posthog;
  if (!postHogReady(ph) || pendingAnalyticsOperations.length === 0) return;

  const operations = pendingAnalyticsOperations;
  pendingAnalyticsOperations = [];
  for (const operation of operations) {
    try {
      if (operation.kind === "capture") {
        ph.capture(operation.event, operation.properties);
      } else if (operation.kind === "identify") {
        ph.identify(operation.id, operation.properties);
      } else {
        ph.reset();
      }
    } catch (err) {
      console.warn("[analytics] queued operation failed", err);
    }
  }
}

export function clearPendingAnalyticsOperations(): void {
  pendingAnalyticsOperations = [];
}

// ─── GA4 event tracking ─────────────────────────────────────────────

/** Fire a GA4 event on the client. No-ops if gtag isn't loaded. */
export function trackGA4Event(
  eventName: string,
  params?: Record<string, unknown>,
) {
  if (!clientAnalyticsEnabled() || !window.gtag) return;
  window.gtag("event", eventName, params);
}
